import { TOP_HOST, WEBAPP_PROTOCOL } from "@/api/_client";
import { useDocumentTypeSuggestionPendingCount } from "@/api/document-templates";
import { useReceivedUserInvitations } from "@/api/invitations";
import { useOrganizations, useAICapStatus } from "@/api/organizations";
import { useUser } from "@/api/user";
import commonForgeLogoWhite from "@/assets/logo-white.svg";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";
import { ConditionalWrapper, cn, initials } from "@/lib/utils";
import { useAppContext } from "@/providers/app-context";
import { ROUTES, SidebarSubitemsT } from "@/routes";
import {
  CheckIcon,
  HelpCircleIcon,
  LayoutDashboardIcon,
  LogOutIcon,
  LucideIcon,
  Settings2Icon,
  SettingsIcon,
  Users2Icon,
  AlertCircle,
} from "lucide-react";
import React from "react";
import { Link, useMatch, useSearchParams, useLocation, useNavigate } from "react-router-dom";
import { NewOrganizationForm } from "./auth/new-organization-form";
import { GettingStartedDialog } from "./getting-started-dialog";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

interface SidebarButtonProps {
  link?: string;
  match?: string;
  name: string;
  Icon?: LucideIcon;
  onClick?: () => void;
  badge?: string;
  className?: string;
  collapsed?: boolean;
  subitems?: SidebarSubitemsT[];
  subitemBadges?: Record<string, number>;
  rightIcon?: LucideIcon;
  rightIconClassName?: string;
}

function SidebarButton({
  link,
  match,
  name,
  Icon,
  onClick,
  badge,
  collapsed,
  className,
  subitems,
  subitemBadges,
  rightIcon: RightIcon,
  rightIconClassName,
}: SidebarButtonProps): JSX.Element {
  const location = useLocation();
  const matchPath = match || link || "__no_match__";
  let selected = useMatch(matchPath);
  // Buttons without a link should never be selected
  if (!match && !link) selected = null;

  // For parent routes with subitems, highlight when any subitem path is active
  if (!selected && subitems?.length) {
    const isSubitemActive = subitems.some((item) =>
      location.pathname.startsWith(item.path.replace(/\/$/, "")),
    );
    if (isSubitemActive) selected = {} as ReturnType<typeof useMatch>;
  }

  const { setShowMobileSidebar, actingOrganization } = useAppContext();
  const [searchParams] = useSearchParams();
  const subItemsFiltered = subitems?.filter((item) => {
    return item.canAccess?.(actingOrganization!);
  });

  const navigate = useNavigate();

  return (
    <ConditionalWrapper
      condition={!!link && !subitems?.length}
      wrapper={(children) => <Link to={link!}>{children}</Link>}
    >
      <Button
        onClick={() => {
          setShowMobileSidebar(false);
          if (subitems?.length && link) {
            navigate(link);
          }
          onClick?.();
        }}
        variant={selected ? "sidebarSelected" : "sidebar"}
        // className="hover:bg-sidebar-foreground/25 w-full justify-start rounded-none hover:text-white"
        className={cn("w-full justify-start rounded-none", className)}
      >
        <div className="relative">
          {badge && collapsed && (
            <Badge
              variant="notification"
              className={`absolute -top-3 scale-[.6]`}
            >
              {badge}
            </Badge>
          )}
          {Icon && <Icon className="mr-2 h-4 w-4" />}
        </div>
        <p
          className={`line-clamp-1 text-left transition-all ${
            collapsed ? "w-0" : "flex-1"
          }`}
        >
          {name}
        </p>
        {badge && !collapsed && (
          <Badge variant="notification" className={`scale-[.8]`}>
            {badge}
          </Badge>
        )}
        {RightIcon && !collapsed && (
          <RightIcon className={cn("ml-auto h-4 w-4", rightIconClassName)} />
        )}
      </Button>
      {subItemsFiltered?.length && subItemsFiltered.length > 0 ? (
        <div
          // prettier-ignore
          className={`overflow-hidden border-l-4 border-l-sidebar-foregroundSelected bg-sidebar-backgroundSelected pl-12 transition-all ${selected && !collapsed ? "max-h-96 pt-4" : "max-h-0 pt-0"}`}
        >
          {subItemsFiltered.map((item) => {
            const isActive = item.selected
              ? item.selected(searchParams)
              : location.pathname.startsWith(item.path.replace(/\/$/, ""));
            const badgeCount = item.badgeKey ? subitemBadges?.[item.badgeKey] : undefined;
            return (
            <Link key={item.path} to={item.path}>
              <div
                className={`flex h-8 ${
                  isActive
                    ? "text-sidebar-foregroundSelected hover:text-sidebar-foregroundSelected"
                    : "hover:text-sidebar-foregroundSelected/85"
                }`}
              >
                <div className="relative bottom-4 my-0 mr-2 h-full w-4 border-b-2 border-l-2 border-sidebar-foregroundSelected" />
                <div className="relative top-1 flex items-center gap-2">
                  {item.title}
                  {badgeCount != null && badgeCount > 0 && (
                    <Badge variant="notification" className="scale-[.7]">
                      {badgeCount}
                    </Badge>
                  )}
                </div>
              </div>
            </Link>
            );
          })}
        </div>
      ) : null}
    </ConditionalWrapper>
  );
}

interface SidebarProps extends React.HTMLAttributes<HTMLDivElement> {
  collapsed?: boolean;
}

export function Sidebar({ className, collapsed }: SidebarProps) {
  const [showOrganizationSwitcher, setShowOrganizationSwitcher] =
    React.useState(false);
  const [showGettingStarted, setShowGettingStarted] = React.useState(false);
  const organizations = useOrganizations();

  const { logOut, actingOrganization } = useAppContext();
  const user = useUser();
  const invitations = useReceivedUserInvitations();

  // Check AI usage limit
  const { costCapReached: isAILimitExceeded } = useAICapStatus();

  // Badge counts for sidebar subitems (admin-only endpoint)
  const { data: docTypeSuggestionsCount } =
    useDocumentTypeSuggestionPendingCount(!!actingOrganization?.is_admin);
  const subitemBadges = React.useMemo(
    () => ({
      docTypeSuggestions: docTypeSuggestionsCount ?? 0,
    }),
    [docTypeSuggestionsCount],
  );
  return (
    <div className={cn("fixed top-0 bg-sidebar pb-2", className)}>
      <div className="flex h-screen flex-col space-y-4 overflow-auto pb-4 scrollbar-thin scrollbar-track-transparent scrollbar-thumb-sidebar-foreground">
        <div className="flex-1 py-2">
          <div className="flex w-full flex-col items-center py-6 text-center text-lg text-white">
            <img
              src={commonForgeLogoWhite}
              className="mb-6 block h-6 sm:hidden"
            />
            {!collapsed ? (
              <>
                <p className="line-clamp-1">
                  {user.data!.first_name} {user.data!.last_name}
                </p>
                {(organizations.data && organizations.data.count > 1) ||
                user.data?.is_overseer ? (
                  <Button
                    onClick={() => setShowOrganizationSwitcher(true)}
                    size="sm"
                    variant={"outlineInverted"}
                    className="mt-1 rounded-full text-sm"
                  >
                    {actingOrganization?.name}
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    variant={"outlineInvertedNoHover"}
                    className="mt-1 rounded-full text-sm hover:cursor-default"
                  >
                    {actingOrganization?.name}
                  </Button>
                )}
              </>
            ) : (
              <Avatar className="mb-4">
                <AvatarFallback className="text-slate-700">
                  {initials(user.data!.first_name, user.data!.last_name)}
                </AvatarFallback>
              </Avatar>
            )}
          </div>
          {ROUTES.filter((r) => !r.hidden).map((route) => {
            if (route.canAccess && !route.canAccess(actingOrganization!)) {
              return null;
            }
            return (
              <div
                className="mb-4 flex flex-col text-sidebar-foreground"
                key={route.path}
              >
                <SidebarButton
                  collapsed={collapsed}
                  link={route.sidebarLink || route.path}
                  match={route.match}
                  name={route.name!}
                  Icon={route.icon}
                  subitems={route.sidebarSubitems}
                  subitemBadges={subitemBadges}
                />
              </div>
            );
          })}
        </div>
        <div>
          {[
            ...(actingOrganization?.is_admin
              ? [
                  <SidebarButton
                    collapsed={collapsed}
                    link="/team-management/members"
                    match="/team-management/*"
                    name="Team management"
                    Icon={Users2Icon}
                  />,
                  <SidebarButton
                    collapsed={collapsed}
                    link={"/organization/details"}
                    match={"/organization/*"}
                    name="Organization Settings"
                    Icon={SettingsIcon}
                    rightIcon={isAILimitExceeded ? AlertCircle : undefined}
                    rightIconClassName="text-red-500"
                  />,
                  <div className="h-0.5 w-full bg-sidebar-foreground" />,
                ]
              : []),
            <SidebarButton
              collapsed={collapsed}
              name="Help"
              Icon={HelpCircleIcon}
              onClick={() => setShowGettingStarted(true)}
            />,
            <SidebarButton
              collapsed={collapsed}
              link={"account/profile"}
              match={"/account/*"}
              name="Account"
              Icon={Settings2Icon}
              badge={
                invitations.data?.length && invitations.data.length > 0
                  ? invitations.data?.length.toString()
                  : undefined
              }
            />,
            <SidebarButton
              collapsed={collapsed}
              name="Log out"
              Icon={LogOutIcon}
              onClick={logOut}
              // className="fixed bottom-2"
            />,
          ].map((item, idx) => (
            <div
              key={`sidebar-extra-${idx}`}
              className="mb-4 flex flex-col space-y-2 text-sidebar-foreground"
            >
              {item}
            </div>
          ))}
        </div>
      </div>
      <OrganizationSwitcherDialog
        isOpen={showOrganizationSwitcher}
        close={() => setShowOrganizationSwitcher(false)}
      />
      <GettingStartedDialog
        open={showGettingStarted}
        onOpenChange={setShowGettingStarted}
      />
    </div>
  );
}

const OrganizationSwitcherDialog = ({
  isOpen,
  close,
}: {
  isOpen?: boolean;
  close?: () => void;
}) => {
  return (
    <Dialog open={isOpen} onOpenChange={() => close?.()}>
      <DialogContent className="max-w-sm" closeCallback={() => close?.()}>
        <DialogTitle>Select an organization</DialogTitle>
        <OrganizationSwitcher />
      </DialogContent>
    </Dialog>
  );
};

export const OrganizationSwitcher = ({ className }: { className?: string }) => {
  const [showNewOrganizationDialog, setShowNewOrganizationDialog] =
    React.useState(false);
  const { actingOrganization } = useAppContext();
  const organizations = useOrganizations();
  const user = useUser();
  const [organizationFilter, setOrganizationFilter] = React.useState("");

  return (
    <div className={className}>
      {organizations.data?.results.length !== undefined &&
        organizations.data?.results.length > 5 && (
          <Input
            placeholder="Search organizations"
            value={organizationFilter}
            onChange={(e) => setOrganizationFilter(e.target.value)}
          />
        )}
      <div className="max-h-[400px] overflow-y-auto">
        <Table className="mt-2">
          <TableBody>
            {organizations.data && organizations.data.count > 1 && (
              <TableRow className="hover:cursor-pointer">
                <Link to={`${WEBAPP_PROTOCOL}://${TOP_HOST}`}>
                  <TableCell className="flex w-full justify-between font-medium">
                    <div className="flex items-center gap-2">
                      <LayoutDashboardIcon className="h-4 w-4" />
                      Admin home
                    </div>
                    {!actingOrganization && <CheckIcon />}
                  </TableCell>
                </Link>
              </TableRow>
            )}
            {organizations.data?.results
              ?.filter((org) =>
                org.name.toLowerCase().includes(organizationFilter),
              )
              .map((org) => (
                <TableRow className="hover:cursor-pointer" key={org.id}>
                  <Link to={`${WEBAPP_PROTOCOL}://${org.slug}.${TOP_HOST}`}>
                    <TableCell className="flex w-full justify-between">
                      {org.name}
                      {actingOrganization?.id == org.id && <CheckIcon />}
                    </TableCell>
                  </Link>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>
      {user.data?.is_overseer && (
        <div className="flex w-full justify-center">
          <Button
            variant="ghostish"
            size="lg"
            onClick={() => setShowNewOrganizationDialog(true)}
          >
            Create a new organization
          </Button>
        </div>
      )}
      <NewOrganizationDialog
        isOpen={showNewOrganizationDialog}
        close={() => setShowNewOrganizationDialog(false)}
      />
    </div>
  );
};

export const NewOrganizationDialog = ({
  isOpen,
  close,
}: {
  isOpen?: boolean;
  close?: () => void;
}) => {
  return (
    <Dialog open={isOpen}>
      <DialogContent className="" closeCallback={() => close?.()}>
        <DialogTitle>Create a new organization</DialogTitle>
        <NewOrganizationForm callback={() => close?.()} />
      </DialogContent>
    </Dialog>
  );
};
